import {
  Stack,
  TextInput,
  Textarea,
  Switch,
  Select,
  SimpleGrid,
  Text,
} from '@mantine/core';
import { useController, type Control, type UseFormWatch } from 'react-hook-form';
import { APP_CONSTRAINTS, HERO_STYLE_OPTIONS, SPOTLIGHT_OPTIONS } from '@/lib/constants/app';
import { usePlayers } from '@/features/players/usePlayers';
import { useAppContentListlists } from '@/features/apps-content/useAppContentListlists';
import type { AppGeneralFormData } from '@/lib/types/App';

interface AppGeneralTabProps {
  control: Control<AppGeneralFormData>;
  watch: UseFormWatch<AppGeneralFormData>;
}

const c = APP_CONSTRAINTS;

export function AppGeneralTab({ control, watch }: AppGeneralTabProps) {
  const { playerOptions } = usePlayers();
  const { contentGroups, categories } = useAppContentListlists();

  // Watch form values for character counters and dynamic selectors
  const nameLength = watch('name')?.length ?? 0;
  const descLength = watch('desc')?.length ?? 0;
  const websiteLength = watch('websiteDomainPath')?.length ?? 0;

  // Build spotlight category options from all available categories
  // Categories are shown regardless of content group assignment;
  // the user selects which category to feature
  const spotlightOptions: { value: string; label: string }[] = [
    { value: SPOTLIGHT_OPTIONS.RANDOM_ANY, label: 'Random (any category)' },
    { value: SPOTLIGHT_OPTIONS.DISABLED, label: 'Disabled' },
    ...categories.map((cat) => ({
      value: String(cat.llr.llId),
      label: cat.llr.title,
    })),
  ];

  // Content group options
  const contentGroupOptions = contentGroups.map((cg) => ({
    value: String(cg.llr.llId),
    label: cg.llr.title,
  }));

  // ── Form field controllers ──

  const { field: nameField, fieldState: nameState } = useController({ control, name: 'name' });
  const { field: descField, fieldState: descState } = useController({ control, name: 'desc' });
  const { field: websiteField, fieldState: websiteState } = useController({
    control,
    name: 'websiteDomainPath',
  });
  const { field: rootLlIdField } = useController({ control, name: 'rootLlId' });
  const { field: playerField } = useController({ control, name: 'defaultPlayerId' });
  const { field: childTargetedField } = useController({ control, name: 'isChildTargeted' });
  const { field: spotlightField } = useController({ control, name: 'spotlightCatLLId' });
  const { field: heroStyleField } = useController({ control, name: 'heroStyle' });

  // Social fields
  const { field: fbField, fieldState: fbState } = useController({ control, name: 'fbId' });
  const { field: igField, fieldState: igState } = useController({ control, name: 'igId' });
  const { field: xField, fieldState: xState } = useController({ control, name: 'xId' });
  const { field: ytField, fieldState: ytState } = useController({ control, name: 'ytId' });
  const { field: gaField, fieldState: gaState } = useController({ control, name: 'gaId' });

  return (
    <Stack gap="lg">
      {/* Core Settings */}
      <TextInput
        label="App Name"
        value={nameField.value}
        onChange={nameField.onChange}
        onBlur={nameField.onBlur}
        error={nameState.error?.message}
        description={`${nameLength}/${c.NAME_MAX}`}
        maxLength={c.NAME_MAX}
        required
      />

      <Textarea
        label="Description"
        value={descField.value}
        onChange={descField.onChange}
        onBlur={descField.onBlur}
        error={descState.error?.message}
        description={`${descLength}/${c.DESC_MAX}`}
        maxLength={c.DESC_MAX}
        rows={3}
      />

      <TextInput
        label="Website URL"
        placeholder="example.com"
        value={websiteField.value}
        onChange={websiteField.onChange}
        onBlur={websiteField.onBlur}
        error={websiteState.error?.message}
        description={`${websiteLength}/${c.WEBSITE_MAX}`}
        maxLength={c.WEBSITE_MAX}
      />

      <SimpleGrid cols={2}>
        <Select
          label="Content Group"
          data={contentGroupOptions}
          value={rootLlIdField.value ? String(rootLlIdField.value) : null}
          onChange={(val) => val && rootLlIdField.onChange(parseInt(val, 10))}
          allowDeselect={false}
          required
        />

        <Select
          label="Default Player"
          data={playerOptions}
          value={playerField.value ? String(playerField.value) : null}
          onChange={(val) => val && playerField.onChange(parseInt(val, 10))}
          allowDeselect={false}
          required
        />
      </SimpleGrid>

      <SimpleGrid cols={2}>
        <Select
          label="Spotlight Category"
          description="Featured content category for the app"
          data={spotlightOptions}
          value={spotlightField.value}
          onChange={(val) => val && spotlightField.onChange(val)}
          allowDeselect={false}
        />

        <Switch
          label="Child-Targeted Content"
          description="Marks the app as containing content directed at children"
          checked={childTargetedField.value}
          onChange={(e) => childTargetedField.onChange(e.currentTarget.checked)}
        />
      </SimpleGrid>

      <Select
        label="Hero Style"
        description="How the featured billboard presents artwork in your TV apps. Poster card shows the complete image over a soft blurred backdrop — the safe choice when your artwork has titles or text baked in. Immersive fills the screen edge-to-edge with the artwork itself — striking for designed promotional art, but TV overscan can crop its edges."
        data={HERO_STYLE_OPTIONS as unknown as { value: string; label: string }[]}
        value={heroStyleField.value}
        onChange={(val) => val && heroStyleField.onChange(val)}
        allowDeselect={false}
      />

      {/* Social Media IDs */}
      <Text fw={600} size="md" mt="md">
        Social Media
      </Text>

      <SimpleGrid cols={2}>
        <TextInput
          label="Facebook ID"
          placeholder="page.name"
          value={fbField.value}
          onChange={fbField.onChange}
          onBlur={fbField.onBlur}
          error={fbState.error?.message}
          maxLength={c.FB_ID_MAX}
        />

        <TextInput
          label="Instagram ID"
          placeholder="username"
          value={igField.value}
          onChange={igField.onChange}
          onBlur={igField.onBlur}
          error={igState.error?.message}
          maxLength={c.IG_ID_MAX}
        />

        <TextInput
          label="X (Twitter) ID"
          placeholder="handle"
          value={xField.value}
          onChange={xField.onChange}
          onBlur={xField.onBlur}
          error={xState.error?.message}
          maxLength={c.X_ID_MAX}
        />

        <TextInput
          label="YouTube ID"
          placeholder="@channel or UCXXXXXXX"
          value={ytField.value}
          onChange={ytField.onChange}
          onBlur={ytField.onBlur}
          error={ytState.error?.message}
          maxLength={c.YT_ID_MAX}
        />
      </SimpleGrid>

      {/* External Platform IDs */}
      <Text fw={600} size="md" mt="md">
        Analytics
      </Text>

      <TextInput
        label="Google Analytics ID"
        placeholder="G-XXXXXXXXXX or UA-XXXXXX-X"
        value={gaField.value}
        onChange={gaField.onChange}
        onBlur={gaField.onBlur}
        error={gaState.error?.message}
        maxLength={c.GA_ID_MAX}
        style={{ maxWidth: 400 }}
      />
    </Stack>
  );
}
